import * as RadixThemes from '@radix-ui/themes'
import * as React from 'react'

/**
 * The menu primitives shared by Radix's `DropdownMenu` and `ContextMenu`.
 *
 * The two are separate implementations backed by separate React contexts, so a
 * `DropdownMenu.Item` rendered inside a `ContextMenu.Content` throws at runtime.
 * Menu bodies that can be opened from either root read their primitives from
 * `useMenu()` rather than importing one namespace directly.
 */
export type MenuNamespace = Pick<
  typeof RadixThemes.DropdownMenu,
  | 'CheckboxItem'
  | 'Content'
  | 'Group'
  | 'Item'
  | 'Label'
  | 'RadioGroup'
  | 'RadioItem'
  | 'Separator'
  | 'Sub'
  | 'SubContent'
  | 'SubTrigger'
>

const MenuNamespaceContext = React.createContext<MenuNamespace>(
  RadixThemes.DropdownMenu,
)

/**
 * Supplies the menu primitives used by everything rendered beneath it. Wrap the
 * body of a `ContextMenu.Content` in this to reuse a menu written against
 * `useMenu()`.
 */
export const MenuNamespaceProvider = MenuNamespaceContext.Provider

/**
 * Returns the primitives for the menu currently being rendered into, defaulting
 * to `DropdownMenu` when no provider sits above.
 */
export const useMenu = (): MenuNamespace =>
  React.useContext(MenuNamespaceContext)
